import React, { useState } from "react";
import { useSelector } from "react-redux";
import get from "lodash/get";

import "./keep-me-informed-modal.m.css";
import CloseIcon from "../../atoms/CloseIcon";

export const KeepMeInformedModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    designation: "",
    city: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const subscriberMetadata = useSelector((state) => get(state, ["subscriberMetadata"], null));
  const member = useSelector((state) => get(state, ["member"], null));

  // Pre-fill form with existing user data when modal opens and handle body scroll lock
  React.useEffect(() => {
    if (isOpen) {
      // Lock body scroll when modal opens
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');

      // Prevent keyboard scrolling
      const preventKeyboardScroll = (e) => {
        // Allow scrolling within the modal content but prevent page scrolling
        const modalContent = document.querySelector('[data-modal-content]');
        if (modalContent && !modalContent.contains(e.target)) {
          const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
          if (scrollKeys.includes(e.key)) {
            e.preventDefault();
          }
        }
      };

      document.addEventListener('keydown', preventKeyboardScroll);

      // Build name from first-name and last-name if available, otherwise use full name
      const firstName = get(subscriberMetadata, 'first-name', '');
      const lastName = get(subscriberMetadata, 'last-name', '');
      let fullName = '';

      if (firstName || lastName) {
        fullName = `${firstName} ${lastName}`.trim();
      } else {
        fullName = get(subscriberMetadata, 'name', '') || get(member, 'name', '');
      }

      setFormData({
        name: fullName,
        email: get(subscriberMetadata, 'email', '') || get(member, 'email', ''),
        phone: get(subscriberMetadata, 'phone', '') || get(member, 'phone', ''),
        company: get(subscriberMetadata, 'organisation', '') || '',
        designation: get(subscriberMetadata, 'designation', '') || get(subscriberMetadata, 'primary-role', '') || '',
        city: get(subscriberMetadata, 'location', '') || '',
      });

      // Cleanup function for this specific modal opening
      return () => {
        document.removeEventListener('keydown', preventKeyboardScroll);
      };
    } else {
      // Restore body scroll when modal closes
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
    }
  }, [isOpen, subscriberMetadata, member]);

  // Global cleanup effect for component unmount
  React.useEffect(() => {
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
    };
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    const requiredFields = ['name', 'email', 'phone', 'company', 'designation', 'city'];
    const missingFields = requiredFields.filter(field => !formData[field].trim());

    if (missingFields.length > 0) {
      alert(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(formData);
      // Reset form and close modal on success
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        designation: "",
        city: ""
      });
      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div styleName="modal-overlay" onClick={handleClose}>
      <div styleName="modal-content" onClick={(e) => e.stopPropagation()} data-modal-content>
        <div styleName="modal-header">
          <button styleName="close-button" onClick={handleClose} disabled={isSubmitting}>
            <CloseIcon strokeWidth="1.25" />
          </button>
        </div>

        <div styleName="form-container">
          <div styleName="form-title">
            <p>Leave your details for updates and registration support</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div styleName="form-field">
              <div styleName="input-field">
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Name*"
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div styleName="form-row">
              <div styleName="form-field half-width">
                <div styleName="input-field">
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Email*"
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              <div styleName="form-field phone-width">
                <div styleName="input-field">
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="Phone*"
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>

            <div styleName="form-field">
              <div styleName="input-field">
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  placeholder="Company*"
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div styleName="form-row">
              <div styleName="form-field half-width">
                <div styleName="input-field">
                  <input
                    type="text"
                    value={formData.designation}
                    onChange={(e) => handleInputChange('designation', e.target.value)}
                    placeholder="Designation*"
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              <div styleName="form-field city-width">
                <div styleName="input-field">
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    placeholder="City*"
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>

            <div styleName="submit-section">
              <button
                type="submit"
                styleName="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Keep me informed"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};